import { UserCircle2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate } from 'react-router-dom';

import { Card, Input, Button as ShButton } from '../../components/shadcn/index.js';
import { Button, FormField, PhoneInput } from '../../components/ui/index.js';
import { useDirection, setLocale } from '../../hooks/useDirection.js';
import { useCustomerAuthStore, type PortalCustomer } from '../../lib/customerAuthStore.js';
import { publicApi } from '../../lib/publicApi.js';
import { useSiteContextStore } from '../../lib/siteContextStore.js';

interface LoginResponse {
  tokens: { accessToken: string; expiresIn: number };
  customer: PortalCustomer;
}

/** Customer-portal sign-in by PHONE + password (slug from the store's custom domain). */
export function PortalLoginPage(): JSX.Element {
  const { t } = useTranslation('portal');
  const { t: tc, i18n } = useTranslation();
  const dir = useDirection();
  const navigate = useNavigate();
  const isAr = i18n.language.startsWith('ar');
  const signedIn = useCustomerAuthStore((s) => !!s.accessToken);
  // Phone is unique only per tenant, so login is slug-scoped. On a store's
  // custom domain the site context resolves the slug; redirect there to sign in.
  const slug = useSiteContextStore((s) => s.tenant?.slug ?? null);
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  if (signedIn) return <Navigate to="/portal/dashboard" replace />;

  const onSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    if (!slug || !phone.trim() || !password.trim()) {
      setError(t('login.error'));
      return;
    }
    setLoading(true);
    try {
      const r = await publicApi().post<LoginResponse>(
        `/${slug}/auth/login`,
        { phone: phone.trim(), password },
        { withCredentials: true },
      );
      useCustomerAuthStore.getState().setSession({
        accessToken: r.data.tokens.accessToken,
        expiresIn: r.data.tokens.expiresIn,
        customer: r.data.customer,
      });
      navigate('/portal/dashboard');
    } catch {
      setError(t('login.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative isolate flex min-h-screen flex-col bg-bg text-fg" dir={dir}>
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(60%_60%_at_50%_0%,var(--color-brand)_0%,transparent_55%)] opacity-15"
      />
      <header className="flex items-center justify-between px-6 py-5 sm:px-10">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-brand text-fg-inverse">
            <UserCircle2 className="h-5 w-5" aria-hidden />
          </div>
          <div className="leading-tight">
            <div className="text-sm font-semibold uppercase tracking-wide">{t('title')}</div>
            <div className="text-xs text-fg-muted">{t('subtitle')}</div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <ShButton variant={isAr ? 'ghost' : 'outline'} size="sm" onClick={() => setLocale('en')} aria-pressed={!isAr} className="h-8 px-3 text-xs">
            EN
          </ShButton>
          <ShButton variant={isAr ? 'outline' : 'ghost'} size="sm" onClick={() => setLocale('ar')} aria-pressed={isAr} className="h-8 px-3 text-xs">
            AR
          </ShButton>
        </div>
      </header>

      <main className="flex flex-1 items-center justify-center px-6 py-10">
        <div className="w-full max-w-md">
          <Card className="p-7">
            <h1 className="text-2xl font-semibold">{t('login.title')}</h1>
            <p className="mt-1 text-sm text-fg-muted">{t('login.subtitlePhone', { defaultValue: 'Sign in with your phone number and password.' })}</p>
            {slug ? (
              <form className="mt-6 flex flex-col gap-4" onSubmit={onSubmit} noValidate>
                <FormField id="portal-phone" label={t('login.phone', { defaultValue: 'Phone number' })} required>
                  <PhoneInput id="portal-phone" value={phone} onChange={setPhone} autoFocus />
                </FormField>
                <FormField id="portal-password" label={t('login.password')} required>
                  <Input id="portal-password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
                </FormField>
                {error && (
                  <p className="rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger" role="alert">
                    {error}
                  </p>
                )}
                <Button type="submit" loading={loading}>{t('login.submit')}</Button>
              </form>
            ) : (
              <p className="mt-6 rounded-md border border-border bg-bg-subtle px-3 py-3 text-sm text-fg-muted">
                {t('login.openStore', { defaultValue: 'Open your store’s website to sign in to your account.' })}
              </p>
            )}
          </Card>
          <p className="mt-4 text-center text-xs text-fg-muted">{tc('welcome', { brand: t('title') })}</p>
        </div>
      </main>
    </div>
  );
}
